import { Plane } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ReEntryPermit = () => {
  const faqs = [
    {
      question: "How long can I stay abroad with a re-entry permit?",
      answer: "A re-entry permit allows you to remain outside the U.S. for up to 2 years without abandoning your permanent resident status. This is crucial for green card holders who need extended international travel for work, family, or other reasons.",
    },
    {
      question: "When should I apply for a re-entry permit?",
      answer: "You must be physically present in the U.S. when you file your re-entry permit application. Apply at least 3-4 months before your planned departure. You can leave the U.S. after biometrics are completed, even if the permit hasn't been approved yet.",
    },
  ];

  const mainContent = (
    <>
      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-bold mb-6">Protect Your Green Card While Traveling Abroad</h2>
        <p className="text-xl text-muted-foreground mb-8">
          If you're a lawful permanent resident (green card holder) who needs to remain outside the United States for an extended period, your permanent residency status may be at risk. A Re-Entry Permit allows you to travel abroad for up to two years without abandoning your permanent resident status.
        </p>

        <h3 className="text-2xl font-bold mb-4">Why You Need a Re-Entry Permit</h3>
        <ul className="list-disc pl-6 mb-6">
          <li>Extended work assignments abroad</li>
          <li>Caring for sick family members overseas</li>
          <li>Educational pursuits or research abroad</li>
          <li>Business ventures requiring international presence</li>
          <li>Avoiding presumption of abandonment of residency</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">Re-Entry Permit vs. Green Card</h3>
        <p className="mb-6">
          While a green card technically allows you to travel, staying outside the U.S. for more than 6 months can trigger questions about residency abandonment. A re-entry permit clearly demonstrates your intent to maintain U.S. permanent residency during extended absences.
        </p>

        <h3 className="text-2xl font-bold mb-4">Application Process</h3>
        <ul className="list-disc pl-6 mb-8">
          <li>File Form I-131 while physically in the U.S.</li>
          <li>Attend biometrics appointment</li>
          <li>Receive permit approval (3-6 months typically)</li>
          <li>Pick up permit at U.S. embassy/consulate or have it mailed</li>
        </ul>

        <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );

  return (
    <GenericPracticePage
      title="Re-Entry Permit"
      subtitle="Travel document for extended trips abroad without losing green card status"
      icon={<Plane className="h-20 w-20 mx-auto mb-6 text-accent" />}
      mainContent={mainContent}
    />
  );
};

export default ReEntryPermit;
